<template>
  <div>
    <Card class=" mt-5">
      <div class="border-b border-gray-200 px-4 py-5 sm:px-6" v-if="session">
        <h3
          class="text-lg md:text-xl font-bold md:text-3xl tracking-wide text-darkBlue "
        >
          {{ session.title }} - {{ session.speakers[0].name }}
          <span class="relative inline-flex h-4 w-4 ml-2">
            <span
              class="relative inline-flex items-center rounded-full h-4 w-4 bg-aspire animate-pulse"
            >
            </span>
          </span>
          <span class="ml-2 text-aspire">Live</span>
        </h3>
      </div>
      <div class="px-4 py-5 sm:p-6 ">
        <div class="flex justify-end mb-2">
          <a
            class="hidden md:inline-block text-sm underline px-2 py-1"
            href="#"
            @click="
              () => {
                showChat = !showChat;
              }
            "
          >
            <span v-if="showChat">Theatre Mode</span>
            <span v-if="showChat === false" class="">Side By Side</span>
          </a>
        </div>

        <div
          class="wrapper"
          :class="{ theatre: !showChat, sidebside: showChat }"
        >
          <div v-if="session.technicalDifficulties" class="video">
            <div class="text-center">
              <h2
                class="text-2xl tracking-wide leading-10 font-extrabold font-open-sans text-red mt-4 sm:text-5xl sm:leading-none md:text-4xl"
              >
                We'll be right back
              </h2>
              <p
                class="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
              >
                We are currently experiencing some technical difficulties. We'll
                be back online as soon as possible.
              </p>
            </div>
          </div>
          <div class="video" v-else>
            <div v-if="session.hideVideo">
              <img :src="previewImage" class="shadow-lg mx-auto" />
            </div>
            <div class="mx-auto embed-container" v-else>
              <iframe
                :src="youtubeURL"
                class="w-full"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
          </div>
          <div class="chatbox">
            <chat-box :session="session" />
          </div>
          <div class="details">
            <div
              class="border-vectric border-4 p-2"
              v-if="hasAlternativeLanguage"
            >
              <span class="font-semibold">
                Note :
              </span>

              <span v-if="shownLang === 'en'">
                Cette vidéo est également disponible en Français. Cliquez
                <span
                  v-on:click="toggleLanguage"
                  class="underline cursor-pointer"
                  >ici</span
                >
                pour regarder.
              </span>
              <span v-if="shownLang === 'fr'">
                This video is available in English. Click
                <span
                  v-on:click="toggleLanguage"
                  class="underline cursor-pointer"
                  >here</span
                >
                to watch
              </span>
            </div>
            <p class="mt-6 text-base leading-6 text-gray-800 prose">
              <standard-block-content
                :blocks="session.description"
              ></standard-block-content>
            </p>
            <div class="mt-10" v-if="hasTutorials">
              <tutorial-videos :session="session"></tutorial-videos>
            </div>
            <div class="mt-8">
              <topic-grid :session="session" :color="topicColor"></topic-grid>

              <div class="mt-10">
                <session-compatibility-block
                  :session="session"
                ></session-compatibility-block>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
import ChatBox from "./ChatBox";
import LiveStreamPlayer from "./LiveStreamPlayer";
import TopicGrid from "./TopicGrid";
import SessionCompatibilityBlock from "./SessionCompatibilityBlock";
import { mapGetters } from "vuex";
import StandardBlockContent from "./StandardBlockContent";
import TutorialVideos from "./TutorialVideos";
import Card from "./shared/Card.vue";
import { colorFromSession } from "../utils/colorFromSession";
import sanityImageBuilder from "../utils/sanityImageBuilder";

export default {
  name: "LiveSessionPage",
  components: {
    Card,
    TutorialVideos,
    StandardBlockContent,
    SessionCompatibilityBlock,
    TopicGrid,
    ChatBox
  },
  props: {
    session: {
      type: Object
    }
  },
  data: function() {
    return {
      showChat: true,
      multiLang: false,
      shownLang: "en"
    };
  },
  computed: {
    ...mapGetters(["loggedIn"]),
    hasAlternativeLanguage() {
      if (!this.session.alternativeVideo) {
        return false;
      }
      if (
        !this.session.alternativeVideo.youtubeURL ||
        this.session.alternativeVideo.youtubeURL === ""
      ) {
        return false;
      }
      return true;
    },
    previewImage() {
      return sanityImageBuilder
        .image(this.session.previewImage)
        .width(400)
        .url();
    },
    youtubeURL() {
      if (!this.hasAlternativeLanguage) {
        return this.session.youtubeEmbedURL;
      }

      if (this.shownLang === "en") {
        return this.session.youtubeEmbedURL;
      } else {
        return this.session.alternativeVideo.youtubeURL;
      }
    },
    hasTutorials() {
      const tutorials = this.session.tutorials || [];
      return tutorials.length > 0;
    },
    topicColor() {
      return colorFromSession(this.session);
    }
  },
  methods: {
    toggleLanguage() {
      if (this.shownLang === "en") {
        this.shownLang = "fr";
      } else {
        this.shownLang = "en";
      }
    }
  }
};
</script>
<style scoped>
.video {
  grid-area: vd;
}

.chatbox {
  grid-area: cb;
}

.details {
  grid-area: dt;
}

.wrapper {
  display: grid;
  grid-auto-rows: minmax(100px, auto);
  grid-template-columns: 1fr;
  grid-row-gap: 10px;
  grid-template-areas:
    "vd"
    "cb"
    "dt";
}

@media only screen and (min-width: 900px) {
  .wrapper.sidebside {
    grid-template-columns: repeat(9, 1fr);
    grid-column-gap: 10px;
    grid-template-areas:
      "vd vd vd vd vd vd cb cb cb"
      "dt dt dt dt dt dt cb cb cb";
  }

  .wrapper.theatre {
    grid-template-columns: minmax(200px, auto);
    grid-column-gap: 10px;
    grid-template-areas:
      "vd"
      "cb"
      "dt";
  }
}

.embed-container {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
}

.embed-container iframe,
.embed-container object,
.embed-container embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
